import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  RESEARCH_CONTINUE_PROMPT_TEMPLATE_V1,
  RESEARCH_PROMPT_TEMPLATE_V1,
} from "#shared/researchDefaults.ts";
import { Button } from "@web/components/ui/button.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { ResearchCategoriesPanel } from "@web/features/settings/ResearchCategoriesPanel.tsx";
import {
  useResearchCategories,
  useResearchTemplates,
  useSaveContinueResearchTemplate,
  useSaveResearchTemplate,
} from "@web/features/ideas/ideasApi.ts";
import { cn } from "@web/lib/utils.ts";

const PLACEHOLDERS = [
  {
    token: "{{ANCHORS}}",
    blurb:
      "Verified facts from the DB for the entity (track, artist, born-as, country, year, label, MB link, Wikipedia…). Omitted when missing — never blank placeholders. Song / artist names live here — there is no separate {{SONG_NAME}} token.",
  },
  {
    token: "{{CATEGORIES}}",
    blurb:
      "Active research angles from Settings → Research (name + question). Inactive angles stay out of future prompts; existing cards keep their tags.",
  },
] as const;

const SAMPLE_ANCHORS = `  Track: Pesse Mi Buntare
  Artist: Yta Jourias
  Artist born as: Germaine Adjovi Amékou
  Artist country: Togo
  Recorded/released: 1975
  Label: Sonafric`;

type EditorKind = "initial" | "continue";

function formatCategoriesPreview(
  cats: { name: string; question: string; active: boolean }[],
): string {
  const active = cats.filter((c) => c.active);
  if (active.length === 0) return "(no active categories — enable some in the list below)";
  const nameWidth = Math.max(...active.map((c) => c.name.length));
  return active
    .map((c) => {
      const pad = " ".repeat(Math.max(0, nameWidth - c.name.length));
      return `- ${c.name}${pad} ${c.question}`;
    })
    .join("\n");
}

function TemplateEditor({
  kind,
  value,
  onChange,
  onSave,
  onDiscard,
  onLoadDefault,
  dirty,
  saving,
}: {
  kind: EditorKind;
  value: string;
  onChange: (next: string) => void;
  onSave: () => void;
  onDiscard: () => void;
  onLoadDefault: () => void;
  dirty: boolean;
  saving: boolean;
}) {
  const missingAnchors = value.length > 0 && !value.includes("{{ANCHORS}}");
  const missingCategories = value.length > 0 && !value.includes("{{CATEGORIES}}");

  function insertToken(token: string) {
    onChange(
      (() => {
        if (value.includes(token)) {
          toast.message(`${token} is already in the template`);
          return value;
        }
        const sep = value.endsWith("\n") || value.length === 0 ? "" : "\n\n";
        return `${value}${sep}${token}\n`;
      })(),
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {PLACEHOLDERS.map((p) => (
          <div
            key={`${kind}-${p.token}`}
            className="rounded-md border border-line bg-panel-elevated px-3 py-2.5"
            title={p.blurb}
          >
            <div className="flex items-center justify-between gap-2">
              <code className="rounded bg-panel px-1.5 py-0.5 font-mono text-xs text-accent">
                {p.token}
              </code>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => insertToken(p.token)}
              >
                Insert
              </Button>
            </div>
            <p className="mt-1.5 text-xs leading-relaxed text-muted">{p.blurb}</p>
          </div>
        ))}
      </div>

      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        className={cn(
          "min-h-80 w-full resize-y rounded-md border border-line bg-panel-elevated px-3 py-3",
          "font-mono text-sm leading-relaxed text-text",
          "placeholder:text-faint focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent",
        )}
      />
      {(missingAnchors || missingCategories) && (
        <p className="text-sm text-status-working">
          Warning: template is missing{" "}
          {[missingAnchors && "{{ANCHORS}}", missingCategories && "{{CATEGORIES}}"]
            .filter(Boolean)
            .join(" and ")}
          . Copy will still work, but that block will be empty.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!dirty || saving || value.trim().length === 0}
          onClick={onSave}
        >
          Save {kind === "continue" ? "continue" : "initial"} template
        </Button>
        <Button type="button" variant="ghost" size="sm" disabled={!dirty || saving} onClick={onDiscard}>
          Discard
        </Button>
        <Button type="button" variant="secondary" size="sm" disabled={saving} onClick={onLoadDefault}>
          Load default
        </Button>
        {dirty && <span className="text-xs text-research">Unsaved changes</span>}
      </div>
    </div>
  );
}

/**
 * Workbench → Prompt: edit global initial + continue clipboard templates.
 * Category vocabulary is shared with Settings (same per-user table).
 */
export function ResearchPromptPanel() {
  const templatesQ = useResearchTemplates();
  const saveInitial = useSaveResearchTemplate();
  const saveContinue = useSaveContinueResearchTemplate();
  const cats = useResearchCategories();

  const [initialDraft, setInitialDraft] = useState("");
  const [continueDraft, setContinueDraft] = useState("");
  const [tab, setTab] = useState<EditorKind>("initial");
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (templatesQ.data) {
      setInitialDraft(templatesQ.data.template);
      setContinueDraft(templatesQ.data.continue_template);
    }
  }, [templatesQ.data]);

  const initialDirty =
    templatesQ.data !== undefined && initialDraft !== templatesQ.data.template;
  const continueDirty =
    templatesQ.data !== undefined && continueDraft !== templatesQ.data.continue_template;

  const categoriesBlock = useMemo(
    () => formatCategoriesPreview(cats.data ?? []),
    [cats.data],
  );

  const previewSource = tab === "initial" ? initialDraft : continueDraft;
  const assembledPreview = useMemo(() => {
    return previewSource
      .replaceAll("{{ANCHORS}}", SAMPLE_ANCHORS)
      .replaceAll("{{CATEGORIES}}", categoriesBlock);
  }, [previewSource, categoriesBlock]);

  return (
    <div className="space-y-8">
      <section className="rounded-lg border border-line bg-panel px-4 py-4">
        <h2 className="text-sm font-semibold">Research prompt templates</h2>
        <p className="mt-1 text-sm text-muted">
          Global clipboard text (admin). Only {"{{ANCHORS}}"} and {"{{CATEGORIES}}"} are
          substituted at copy time. Categories themselves are per-user — edit in Settings or
          below.
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant={tab === "initial" ? "default" : "secondary"}
            onClick={() => setTab("initial")}
          >
            Initial research
          </Button>
          <Button
            type="button"
            size="sm"
            variant={tab === "continue" ? "default" : "secondary"}
            onClick={() => setTab("continue")}
          >
            Continue research
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setShowPreview((v) => !v)}
          >
            {showPreview ? "Hide preview" : "Preview assembled"}
          </Button>
        </div>

        {templatesQ.isLoading && <Skeleton className="mt-4 h-64 w-full" />}

        {templatesQ.isSuccess && tab === "initial" && (
          <div className="mt-4">
            <TemplateEditor
              kind="initial"
              value={initialDraft}
              onChange={setInitialDraft}
              dirty={initialDirty}
              saving={saveInitial.isPending}
              onSave={() => saveInitial.mutate(initialDraft)}
              onDiscard={() => setInitialDraft(templatesQ.data.template)}
              onLoadDefault={() => {
                if (
                  !window.confirm(
                    "Replace the editor with the built-in initial template? Save afterward to persist.",
                  )
                ) {
                  return;
                }
                setInitialDraft(RESEARCH_PROMPT_TEMPLATE_V1);
              }}
            />
          </div>
        )}

        {templatesQ.isSuccess && tab === "continue" && (
          <div className="mt-4">
            <TemplateEditor
              kind="continue"
              value={continueDraft}
              onChange={setContinueDraft}
              dirty={continueDirty}
              saving={saveContinue.isPending}
              onSave={() => saveContinue.mutate(continueDraft)}
              onDiscard={() => setContinueDraft(templatesQ.data.continue_template)}
              onLoadDefault={() => {
                if (
                  !window.confirm(
                    "Replace the editor with the built-in continue template? Save afterward to persist.",
                  )
                ) {
                  return;
                }
                setContinueDraft(RESEARCH_CONTINUE_PROMPT_TEMPLATE_V1);
              }}
            />
          </div>
        )}

        {showPreview && templatesQ.isSuccess && (
          <div className="mt-4 space-y-2">
            <p className="text-xs font-semibold tracking-wider text-faint uppercase">
              Assembled preview ({tab}) — sample anchors + your active categories
            </p>
            <pre className="max-h-96 overflow-auto rounded-md border border-line bg-panel-elevated px-3 py-3 font-mono text-xs leading-relaxed whitespace-pre-wrap text-muted">
              {assembledPreview}
            </pre>
          </div>
        )}
      </section>

      <ResearchCategoriesPanel />
    </div>
  );
}
